import React, { useEffect, useState } from "react";
import axios from "axios";
import QRCode from "qrcode";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, Loader2, XCircle, BadgeCheck, Landmark, Repeat } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtUSD = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtTs = (s) => { try { return s ? new Date(s).toUTCString() : "—"; } catch { return s || "—"; } };

const Row = ({ label, value, mono }) => (
  <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-2.5 border-b border-[#2A2A22]">
    <div className="sm:w-56 shrink-0 text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A]">{label}</div>
    <div className={`text-[13px] text-[#ECECE4] ${mono ? "font-mono text-[11px] break-all" : ""}`}>{value}</div>
  </div>
);

export default function SecuritiesVerify() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [qr, setQr] = useState("");

  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(window.location.href, { margin: 1, width: 200, errorCorrectionLevel: "H" }).then((u) => alive && setQr(u)).catch(() => {});
    (async () => {
      try {
        const r = await axios.get(`${API}/securities/verify/${token}`);
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "This securities record could not be verified.");
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const isLoan = data?.kind === "loan";
  const isTradeline = data?.kind === "tradeline";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#ECECE4] px-4 py-10" data-testid="securities-verify-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="text-[12px] text-[#8A8A8A] hover:text-[#ECECE4]" data-testid="sv-home-link">← Return to Home</Link>
        <div className="mt-5 bg-[#13130E] border border-[#2A2A22] rounded-xl overflow-hidden">
          <div className="px-7 py-6 border-b border-[#2A2A22] flex items-center gap-3">
            {isLoan ? <Repeat size={22} className="text-[#C9A227]" /> : <Landmark size={22} className="text-[#B8860B]" />}
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8A8A]">SEUTRADE FINANCIAL SERVICES · Securities Desk</div>
              <div className="text-xl font-serif">{isTradeline ? "Tradeline Verification" : isLoan ? "Securities Loan Verification" : "Securities Account Verification"}</div>
            </div>
          </div>
          {error ? (
            <div className="px-7 py-12 text-center" data-testid="sv-error">
              <XCircle size={34} className="text-[#FF8585] mx-auto mb-3" />
              <div className="text-[#FF8585] text-sm">{error}</div>
            </div>
          ) : !data ? (
            <div className="px-7 py-16 text-center" data-testid="sv-loading"><Loader2 className="animate-spin inline text-[#B8860B]" /></div>
          ) : (
            <div className="px-7 py-6" data-testid="sv-result">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-5 text-[11px] uppercase tracking-[0.14em] font-bold bg-[#0B6B4F]/15 text-[#3DBB8E] border border-[#0B6B4F]/50 rounded-full" data-testid="sv-verified-badge">
                <BadgeCheck size={14} /> Verified
              </div>
              {isTradeline ? (
                <>
                  <Row label="Tradeline No." value={<span className="font-semibold text-[#E4C97A]">{data.tradeline_no}</span>} />
                  <Row label="Instrument" value={data.instrument_type} />
                  <Row label="From Account" value={data.account_no} />
                  <Row label="Instrument Face Value" value={fmtUSD(data.face_amount)} />
                  <Row label="Tradeline Price Paid" value={fmtUSD(data.price_paid)} />
                  <Row label="Holder" value={data.buyer_name || "—"} />
                  <Row label="Status" value={<span className="uppercase">{data.status}</span>} />
                  <Row label="Purchased (UTC)" value={fmtTs(data.purchased_at)} />
                </>
              ) : isLoan ? (
                <>
                  <Row label="Loan No." value={<span className="font-semibold text-[#E4C97A]">{data.loan_no}</span>} />
                  <Row label="From Account" value={data.account_no} />
                  <Row label="Instrument" value={data.account_type} />
                  {data.beneficiary_name && <Row label="Beneficiary" value={data.beneficiary_name} />}
                  <Row label="Lender (Account Holder)" value={data.lender || "—"} />
                  <Row label="Borrower (Counterparty)" value={data.counterparty || "—"} />
                  <Row label="Loaned Amount" value={fmtUSD(data.amount)} />
                  <Row label="Term" value={`${data.term_days || "—"} days`} />
                  <Row label="Loan Fee" value={`${data.counterparty_fee_rate || 0}% p.a. · ${fmtUSD(data.counterparty_fee)}`} />
                  <Row label="Maturity" value={data.maturity_date || "—"} />
                  <Row label="Status" value={<span className="uppercase">{(data.status || "").replace("_", " ")}</span>} />
                  <Row label="Fully Executed" value={data.fully_executed ? "✓ Both parties e-signed" : "Pending signatures"} />
                  <Row label="Executed (UTC)" value={fmtTs(data.executed_at)} />
                </>
              ) : (
                <>
                  <Row label="Account No." value={<span className="font-semibold text-[#E4C97A]">{data.account_no}</span>} />
                  <Row label="Account Type" value={data.account_type} />
                  <Row label="Holder" value={data.holder || "—"} />
                  <Row label="Collateral Balance" value={fmtUSD(data.collateral_balance)} />
                  <Row label="On Loan" value={fmtUSD(data.on_loan)} />
                  <Row label="Available" value={fmtUSD(data.available)} />
                  <Row label="Status" value={<span className="uppercase">{data.status}</span>} />
                  <Row label="Provisioned (UTC)" value={fmtTs(data.provisioned_at)} />
                </>
              )}
              {isLoan && data.notary && (
                <Row label="Notary / Transfer Agent" value={<span className="text-[#E4C97A]">{data.notary.name} · {data.notary.commission_no}</span>} />
              )}
              <Row label="SHA-256" value={data.sha256 || "—"} mono />
              {qr && (
                <div className="mt-5 flex items-center gap-4" data-testid="sv-qr">
                  <img src={qr} alt="verification qr" className="w-24 h-24 rounded bg-white p-2" />
                  <p className="text-[11px] text-[#8A8A8A] leading-relaxed max-w-sm">Scan this QR to re-open this verification certificate at any time. It confirms the SEUTRADE FINANCIAL SERVICES securities record above and does not transfer or negotiate any instrument.</p>
                </div>
              )}
              <p className="mt-5 text-[11px] text-[#5C5C5C] leading-relaxed">
                This read-only certificate confirms the SEUTRADE FINANCIAL SERVICES securities record above. It does not transfer or negotiate any instrument. Retain this reference for your records.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
